"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EditModeDashboardKpi = void 0;
var tslib_1 = require("tslib");
// (C) 2022 GoodData Corporation
var react_1 = tslib_1.__importStar(require("react"));
var classnames_1 = tslib_1.__importDefault(require("classnames"));
var react_intl_1 = require("react-intl");
var sdk_model_1 = require("@gooddata/sdk-model");
var sdk_ui_1 = require("@gooddata/sdk-ui");
var model_1 = require("../../../../model");
var presentationComponents_1 = require("../../../presentationComponents");
var dashboardContexts_1 = require("../../../dashboardContexts");
var common_1 = require("../../common");
var common_2 = require("../common");
var useOptimisticMeasureUpdate_1 = require("./useOptimisticMeasureUpdate");
var EditModeKpiHeadline_1 = require("./EditModeKpiHeadline");
var useKpiDrill_1 = require("../common/useKpiDrill");
exports.EditModeDashboardKpi = function (props) {
    var kpiWidget = props.kpiWidget, customBackend = props.backend, customWorkspace = props.workspace, CustomErrorComponent = props.ErrorComponent, CustomLoadingComponent = props.LoadingComponent, onError = props.onError;
    var intl = react_intl_1.useIntl();
    var _a = dashboardContexts_1.useDashboardComponentsContext({
        ErrorComponent: CustomErrorComponent,
        LoadingComponent: CustomLoadingComponent,
    }), ErrorComponent = _a.ErrorComponent, LoadingComponent = _a.LoadingComponent, KpiWidgetComponentSet = _a.KpiWidgetComponentSet;
    var KpiConfigurationComponent = KpiWidgetComponentSet.configuration.WidgetConfigPanelComponent;
    var _b = useOptimisticMeasureUpdate_1.useOptimisticMeasureUpdate(kpiWidget), isChangingMeasure = _b.isChangingMeasure, titleToShow = _b.titleToShow;
    var backend = sdk_ui_1.useBackendStrict(customBackend);
    var workspace = sdk_ui_1.useWorkspaceStrict(customWorkspace);
    var dashboardFilters = model_1.useDashboardSelector(model_1.selectFilterContextFilters);
    var _c = common_2.useKpiData({ kpiWidget: kpiWidget, dashboardFilters: dashboardFilters }), kpiDataError = _c.error, kpiDataResult = _c.result, kpiDataStatus = _c.status;
    var _d = kpiDataResult !== null && kpiDataResult !== void 0 ? kpiDataResult : {}, primaryMeasure = _d.primaryMeasure, secondaryMeasure = _d.secondaryMeasure, effectiveFilters = _d.effectiveFilters;
    var enableCompactSize = model_1.useDashboardSelector(model_1.selectEnableWidgetCustomHeight);
    var separators = model_1.useDashboardSelector(model_1.selectSeparators);
    var disableDrillUnderline = model_1.useDashboardSelector(model_1.selectDisableKpiDashboardHeadlineUnderline);
    var isDrillable = kpiWidget.drills.length > 0;
    var isSaving = model_1.useDashboardSelector(model_1.selectIsDashboardSaving);
    var isEditable = !isSaving;
    var dispatch = model_1.useDashboardDispatch();
    var coordinates = model_1.useDashboardSelector(model_1.selectWidgetCoordinatesByRef(sdk_model_1.widgetRef(kpiWidget)));
    var onWidgetDelete = react_1.useCallback(function () {
        dispatch(model_1.uiActions.openKpiDeleteDialog(coordinates));
    }, [dispatch, coordinates]);
    var measures = model_1.useDashboardSelector(model_1.selectAllCatalogMeasuresMap);
    var currentMeasure = measures.get(kpiWidget.kpi.metric);
    var onWidgetTitleChanged = react_1.useCallback(function (newTitle) {
        if (newTitle) {
            dispatch(model_1.changeKpiWidgetHeader(kpiWidget.ref, { title: newTitle }));
        }
        else if (currentMeasure) {
            dispatch(model_1.changeKpiWidgetHeader(kpiWidget.ref, { title: currentMeasure.measure.title }));
        }
    }, [currentMeasure, dispatch, kpiWidget.ref]);
    var _e = common_2.useKpiExecutionDataView({
        backend: backend,
        workspace: workspace,
        primaryMeasure: primaryMeasure,
        secondaryMeasure: secondaryMeasure,
        effectiveFilters: effectiveFilters,
        shouldLoad: kpiDataStatus === "success",
    }), error = _e.error, result = _e.result, status = _e.status;
    var isLoading = isChangingMeasure ||
        status === "loading" ||
        status === "pending" ||
        kpiDataStatus === "loading" ||
        kpiDataStatus === "pending";
    var executionsHandler = model_1.useWidgetExecutionsHandler(sdk_model_1.widgetRef(kpiWidget));
    var _f = model_1.useWidgetSelection(sdk_model_1.widgetRef(kpiWidget)), isSelectable = _f.isSelectable, isSelected = _f.isSelected, onSelected = _f.onSelected, hasConfigPanelOpen = _f.hasConfigPanelOpen;
    var isEmbedded = model_1.useDashboardSelector(model_1.selectIsEmbedded);
    var disableDefaultDrills = model_1.useDashboardSelector(model_1.selectDisableDefaultDrills);
    var isClickEnabled = isSelected && !isEmbedded && !disableDefaultDrills;
    var onDrill = useKpiDrill_1.useKpiDrill(kpiWidget);
    var handleOnDrill = react_1.useCallback(function (drillContext) {
        return onDrill({
            dataView: result === null || result === void 0 ? void 0 : result.dataView,
            drillContext: drillContext,
            drillDefinitions: kpiWidget.drills,
            widgetRef: sdk_model_1.widgetRef(kpiWidget),
        });
    }, [onDrill, result, kpiWidget]);
    var handlePanelClosed = react_1.useCallback(function () { return dispatch(model_1.uiActions.setConfigurationPanelOpened(false)); }, [dispatch]);
    var renderBeforeContent = react_1.useMemo(function () {
        var hasConfigComponent = !!KpiConfigurationComponent;
        var shouldHaveConfigRendered = isSelected && hasConfigPanelOpen;
        if (!hasConfigComponent || !shouldHaveConfigRendered) {
            return undefined;
        }
        return function KpiConfiguration() {
            return (react_1.default.createElement(common_1.ConfigurationBubble, { onClose: handlePanelClosed },
                react_1.default.createElement(KpiConfigurationComponent, { widget: kpiWidget })));
        };
    }, [KpiConfigurationComponent, hasConfigPanelOpen, isSelected, kpiWidget, handlePanelClosed]);
    react_1.useEffect(function () {
        if (error) {
            onError === null || onError === void 0 ? void 0 : onError(error);
            executionsHandler.onError(error);
        }
    }, [error, executionsHandler, onError]);
    react_1.useEffect(function () {
        if (result) {
            // empty data is considered an error for execution handling
            if (result.rawData().isEmpty()) {
                executionsHandler.onError(new sdk_ui_1.NoDataSdkError());
            }
            else {
                executionsHandler.onSuccess(result.result(), result.warnings());
            }
        }
    }, [result]);
    return (react_1.default.createElement(presentationComponents_1.DashboardItemKpi, { visualizationClassName: classnames_1.default("s-dashboard-kpi-component", "widget-loaded", "visualization", {
            "kpi-with-pop": kpiWidget.kpi.comparisonType !== "none",
            "content-loading": isLoading,
            "content-loaded": !isLoading,
            "is-editable": isEditable,
        }), contentClassName: classnames_1.default({ "is-editable": isEditable }), renderBeforeContent: renderBeforeContent, renderAfterContent: function () {
            if (isSelected) {
                return (react_1.default.createElement("div", { className: "dash-item-action dash-item-action-delete gd-icon-trash", onClick: onWidgetDelete }));
            }
            return null;
        }, renderHeadline: function (clientHeight) { return (react_1.default.createElement(EditModeKpiHeadline_1.EditableKpiHeadline, { title: titleToShow, clientHeight: clientHeight, onTitleChange: onWidgetTitleChanged })); }, isSelectable: isSelectable, isSelected: isSelected, onSelected: onSelected }, function () {
        var _a;
        if (kpiDataStatus === "loading" || kpiDataStatus === "pending") {
            return react_1.default.createElement(LoadingComponent, null);
        }
        if (kpiDataStatus === "error") {
            return react_1.default.createElement(ErrorComponent, { message: kpiDataError.message });
        }
        return (react_1.default.createElement(common_2.KpiRenderer, { kpi: kpiWidget, kpiResult: common_2.getKpiResult(result, primaryMeasure, secondaryMeasure, separators), filters: (_a = kpiDataResult === null || kpiDataResult === void 0 ? void 0 : kpiDataResult.effectiveFilters) !== null && _a !== void 0 ? _a : [], separators: separators, enableCompactSize: enableCompactSize, error: error, errorHelp: intl.formatMessage({ id: "kpi.error.view" }), isLoading: isLoading, onDrill: handleOnDrill, isDrillable: isDrillable, isKpiValueClickDisabled: !isClickEnabled, disableDrillUnderline: disableDrillUnderline }));
    }));
};
//# sourceMappingURL=EditModeDashboardKpi.js.map